import React from 'react';
import { Box, ButtonBase, Drawer, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme/tokens';
import type { AddableWidget, DashboardShell } from '../widgetLayout';
import type { DashboardLayoutController } from '../useDashboardLayout';

const { surface, line, ink, accent, signal, radius, shadow } = tokens;

/** Drag ids of panel rows carry this prefix so the grid can tell them from tiles. */
export const PANEL_PREFIX = 'panel:';

const PanelRow: React.FC<{
  item: AddableWidget;
  label: string;
  controller: DashboardLayoutController;
}> = ({ item, label, controller }) => {
  const { t } = useTranslation();
  const tt = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({ id: `${PANEL_PREFIX}${item.def.id}`, disabled: item.locked });

  return (
    <Box
      ref={setNodeRef}
      {...(item.locked ? {} : listeners)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        p: '10px 10px 10px 8px',
        border: `1px solid ${line.default}`,
        borderRadius: `${radius.control}px`,
        bgcolor: surface.card,
        cursor: item.locked ? 'default' : 'grab',
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'manipulation',
        '&:hover': { borderColor: accent.border, boxShadow: '0 2px 8px rgba(15,23,42,.06)' },
      }}
    >
      <Box
        {...(item.locked ? {} : attributes)}
        aria-label={item.locked ? undefined : tt('dashboardLayout.moveTile', { title: label })}
        sx={{ color: ink.faint, display: 'grid', placeItems: 'center' }}
      >
        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden>
          <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" />
          <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" />
          <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" />
        </svg>
      </Box>
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: ink.primary }}>{label}</Typography>
      </Box>
      {item.locked ? (
        <ButtonBase
          component={RouterLink}
          to="/app/billing"
          sx={{ fontSize: 11.5, fontWeight: 600, color: signal.caution.ink, bgcolor: signal.caution.tint, border: `1px solid ${signal.caution.border}`, borderRadius: `${radius.chip}px`, px: '7px', py: '2px' }}
        >
          {tt('dashboardLayout.upgrade')}
        </ButtonBase>
      ) : (
        <ButtonBase
          onClick={() => controller.add(item.def.id)}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{ height: 28, px: '10px', border: `1px solid ${accent.border}`, bgcolor: accent.tint, borderRadius: `${radius.small}px`, fontSize: 12.5, fontWeight: 600, color: accent.hover, '&:hover': { bgcolor: accent.tintStrong } }}
        >
          {tt('dashboardLayout.add')}
        </ButtonBase>
      )}
    </Box>
  );
};

/**
 * Widgets that are not on the dashboard (`addableWidgets`, including ones the user
 * hid), as a 300px panel that pushes the content aside. Rows drag onto the grid or
 * add at the end. A plan-locked widget shows Upgrade and cannot be dragged.
 */
export const WidgetDrawer: React.FC<{
  controller: DashboardLayoutController;
  labelOf: (id: string) => string;
  shell: DashboardShell;
}> = ({ controller, labelOf }) => {
  const { t } = useTranslation();
  const tt = t as unknown as (key: string) => string;
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down('md'));
  const panel = (
    <Box
      component="aside"
      aria-label={tt('dashboardLayout.panelTitle')}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: stacked ? 'min(320px, 100vw)' : 300,
        flex: '0 0 auto',
        bgcolor: surface.card,
        borderLeft: `1px solid ${line.default}`,
        ...(stacked ? { height: '100%' } : { position: 'sticky', top: 56, height: 'calc(100vh - 56px)', boxShadow: shadow.card }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '16px 16px 6px' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: ink.primary, flex: '1 1 auto' }}>{tt('dashboardLayout.panelTitle')}</Typography>
        <ButtonBase
          aria-label={tt('dashboardLayout.panelClose')}
          onClick={controller.closeDrawer}
          sx={{ width: 30, height: 30, borderRadius: `${radius.small}px`, color: ink.muted, '&:hover': { bgcolor: surface.tint } }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </ButtonBase>
      </Box>
      <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: ink.muted, p: '0 16px 12px' }}>{tt('dashboardLayout.panelHelp')}</Typography>
      <Box sx={{ flex: '1 1 auto', overflow: 'auto', p: '0 12px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {controller.addable.map((item) => (
          <PanelRow key={item.def.id} item={item} label={labelOf(item.def.id)} controller={controller} />
        ))}
        {controller.addable.length === 0 && (
          <Typography sx={{ py: 2.5, px: 1, textAlign: 'center', fontSize: 12.5, color: ink.muted }}>{tt('dashboardLayout.panelEmpty')}</Typography>
        )}
      </Box>
    </Box>
  );
  // Below md there is no room beside the grid: the same panel opens as a drawer over it.
  return stacked ? (
    <Drawer anchor="right" open onClose={controller.closeDrawer}>
      {panel}
    </Drawer>
  ) : (
    panel
  );
};
